import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { FilterType } from '@src/app/shared/components/table-filter/table-filter.component';
import { Column } from '@src/app/shared/services/table.setting.service';
import { FiltersPanelChangeEvent, FiltersPanelDropdownLazyLoadEvent, FiltersPanelFieldDefinition,FiltersPanelFieldOverrides, FiltersPanelStorageStrategy, FiltersPanelBackendFilter } from './filters-panel.models';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { DropdownOptionId, DropdownOptionLabelStub, DropdownOptionStubsByFieldKey, StoredFiltersPanelState, clearFiltersPanelState, getFiltersPanelStorageKey, loadStoredFiltersPanelState, persistFiltersPanelState } from './filters-panel-storage.helper';

export type DropdownOptionsLazyLoadRequest = {
  pageIndex: number;
  pageSize: number;
  search: string;
};

@Component({
    selector: 'app-filters-panel',
    templateUrl: './filters-panel.component.html',
    styleUrls: ['./filters-panel.component.scss'],
    host: {
        class: 'app-filters-panel',
        '[class.is-open]': 'isOpen'
    },
    standalone: false
})
export class FiltersPanelComponent implements OnInit, OnChanges {

  private lastStoredState: StoredFiltersPanelState | null = null;

  @Input() columns: Column[] = [];

  @Input() storageKeyStrategy: FiltersPanelStorageStrategy = 'route';

  @Input() tableName?: string;

  // Per-field panel overrides (remote paging, local dropdown configuration...)
  // Keys MUST match: (col.filterField ?? col.field)
  @Input() fieldOverrides: FiltersPanelFieldOverrides = {};

  @Output() panelChange = new EventEmitter<FiltersPanelChangeEvent>();

  @Output() dropdownLazyLoad = new EventEmitter<FiltersPanelDropdownLazyLoadEvent>();

  public isOpen = false;

  public form: FormGroup = this.fb.group({});
  public fields: FiltersPanelFieldDefinition[] = [];

  public readonly filterTypes = FilterType;

  constructor(
    private fb: FormBuilder,
    private router: Router
  ) { }

  ngOnInit() {
    this.rebuildFromColumns();

    const restoredWithFilters = this.restoreFromStorageAndEmit();
    if (restoredWithFilters) {
      this.isOpen = true;
    }
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns']) {
      this.rebuildFromColumns();
      this.restoreFromStorageAndEmit();
      return;
    }

    // if key inputs arrive later, restore again
    if (changes['tableName'] || changes['storageKeyStrategy']) {
      this.restoreFromStorageAndEmit();
      return;
    }

    // bugfix: DO NOT rebuild the form on overrides changes or it kills focus!!
    if (changes['fieldOverrides']) {
      this.applyOverridesToExistingFields();

      const stored = this.lastStoredState ?? this.loadStoredState();

      if (stored) {
        this.lastStoredState = stored;
        this.applyStoredOptionStubsToFields(stored);
      }

      return;
    }
  }

  private applyOverridesToExistingFields() {
    const overrides = this.fieldOverrides ?? {};

    for (const field of this.fields) {
      if (field.filterType !== FilterType.CustomDropdown) {
        continue;
      }

      const override = overrides[field.fieldKey];
      if (!override) {
        continue;
      }

      field.override = override;
      field.options = override.options ?? field.options ?? [];

      field.optionLabel = override.optionLabel ?? field.optionLabel ?? 'text';
      field.optionValue = override.optionValue ?? field.optionValue ?? 'id';
      field.isMultiselect = override.isMultiselect ?? field.isMultiselect ?? true;
    }
  }

  onSubmitFilters() {
    const filters = this.mapFormToBackendFilters();
    this.persistToStorage();
    this.panelChange.emit({ source: 'apply', filters });
  }

  onResetFilters() {
    this.form.reset(this.buildEmptyFormDefaults());
    this.clearFromStorage();
    this.panelChange.emit({ source: 'clear', filters: [] });
  }

  onCustomDropdownLazyLoad(fieldKey: string, request: DropdownOptionsLazyLoadRequest) {
    this.dropdownLazyLoad.emit({ fieldKey, ...request });
  }

  // Build fields + form dynamically from visible columns
  private rebuildFromColumns() {
    const filterableColumns = this.getFilterableColumns(this.columns);

    const newFields: FiltersPanelFieldDefinition[] = [];

    for (const col of filterableColumns) {
      const fieldKey = (col.filterField ?? col.field).trim();
      const controlName = this.toSafeControlName(fieldKey);

      // panelFilter override table filter type
      const effectiveFilterType: FilterType = (col.panelFilter ?? col.filter) as FilterType;
      const operator = this.resolveOperatorForField(effectiveFilterType, col.operator);

      // filter fields to render
      switch (effectiveFilterType) {
        case FilterType.CustomDropdown: {
          const override = this.fieldOverrides[fieldKey];

          newFields.push({
            fieldKey,
            controlName,
            labelKey: `TABLE.FILTER.${col.header}`,
            filterType: FilterType.CustomDropdown,
            operator,

            options: override?.options ?? [],
            optionLabel: override?.optionLabel ?? 'text',
            optionValue: override?.optionValue ?? 'id',
            isMultiselect: override?.isMultiselect ?? true,
            override
          });
          break;
        }

        case FilterType.Text: {
          newFields.push({
            fieldKey,
            controlName,
            labelKey: `TABLE.FILTER.${col.header}`,
            filterType: FilterType.Text,
            operator
          });
          break;
        }

        case FilterType.Date: {
          newFields.push({
            fieldKey,
            controlName,
            labelKey: `TABLE.FILTER.${col.header}`,
            filterType: FilterType.Date,
            operator
          });
          break;
        }

        case FilterType.Numeric: {
          newFields.push({
            fieldKey,
            controlName,
            labelKey: `TABLE.FILTER.${col.header}`,
            filterType: FilterType.Numeric,
            operator
          });
          break;
        }

        default: {
          console.warn(`[FiltersPanel] Unsupported filter type:`, effectiveFilterType, 'for column', col);
          continue; // skip unsupported types
        }
      }
    }

    this.fields = newFields;

    const formGroupConfig: Record<string, FormControl> = {};
    const defaults = this.buildEmptyFormDefaults();

    for (const field of this.fields) {
      formGroupConfig[field.controlName] = new FormControl(defaults[field.controlName]);
    }

    this.form = this.fb.group(formGroupConfig);
  }

  private getFilterableColumns(columns: Column[]): Column[] {
    const safeColumns = Array.isArray(columns) ? columns : [];

    return safeColumns.filter((col) => {
      if (!col) return false;

      // Exclude actions col
      if ((col.field ?? '').toLowerCase() === 'actions') return false;

      const baseFilterType = col.filter;

      // Must have at least a base filter
      if (baseFilterType == null) return false;

      // Filters panel
      const panelFilterType = col.panelFilter;

      if (panelFilterType === FilterType.CustomDropdown) {
        return true;
      }

      if (baseFilterType === FilterType.Text) {
        return true;
      }

      if (baseFilterType === FilterType.Date) {
        return true;
      }

      if (baseFilterType === FilterType.Numeric) {
        return true;
      }

      return false;
    });
  }

  private resolveOperatorForField(filterType: FilterType, columnOperator: any): string {
    const normalizedColumnOperator = (columnOperator ?? '').toString().trim();

    if (normalizedColumnOperator.length > 0) {
      return normalizedColumnOperator;
    }

    if (filterType === FilterType.Text) {
      return FilterOperator.CONTAINS;
    }

    if (filterType === FilterType.CustomDropdown) {
      return FilterOperator.IN;
    }

    if (filterType === FilterType.Date) {
      return FilterOperator.IN_RANGE;
    }

    if (filterType === FilterType.Numeric) {
      return FilterOperator.EQUALS;
    }

    return FilterOperator.CONTAINS;
  }

  private buildEmptyFormDefaults(): Record<string, any> {
    const defaults: Record<string, any> = {};

    for (const field of this.fields) {
      if (field.filterType === FilterType.CustomDropdown) {
        defaults[field.controlName] = [];
        continue;
      }

      if (field.filterType === FilterType.Date) {
        defaults[field.controlName] = null; // primeNg empty date
        continue;
      }

      if (field.filterType === FilterType.Numeric) {
        defaults[field.controlName] = null;
        continue;
      }

      defaults[field.controlName] = '';
    }

    return defaults;
  }

  private toSafeControlName(fieldKey: string): string {
    return fieldKey.replace(/[^\w]/g, '__');
  }

  // Form filters: Text + CustomDropdown
  private mapFormToBackendFilters(): FiltersPanelBackendFilter[] {
    const result: Array<{ propertyName: string; operator: string; values: string[] }> = [];

    const raw = this.form.getRawValue();

    for (const field of this.fields) {
      const controlValue = raw[field.controlName];

      // Text
      if (field.filterType === FilterType.Text) {
        const text = String(controlValue ?? '').trim();
        if (text.length === 0) continue;

        result.push({
          propertyName: field.fieldKey,
          operator: field.operator,
          values: [text]
        });

        continue;
      }

      // Date (range)
      if (field.filterType === FilterType.Date) {

        // PrimeNG range returns: [Date, Date] (or [Date, null])
        const range = Array.isArray(controlValue) ? controlValue : null;
        const startDate = range?.[0];
        const endDate = range?.[1] ?? range?.[0];

        if (!startDate || typeof startDate?.getMonth !== 'function') {
          continue;
        }

        const start = new Date(Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0, 0, 0, 0
        ));

        const end = new Date(Date.UTC(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          23, 59, 59, 999
        ));

        result.push({
          propertyName: field.fieldKey,
          operator: field.operator,
          values: [start.toISOString(), end.toISOString()]
        });

        continue;
      }

      // CustomDropdown (always array -> backend expects strings)
      if (field.filterType === FilterType.CustomDropdown) {
        if (!Array.isArray(controlValue)) continue;

        const ids = controlValue
          .filter((v: any) => v != null)
          .map((v: any) => String(v));

        if (ids.length === 0) continue;

        result.push({
          propertyName: field.fieldKey,
          operator: field.operator,
          values: ids
        });

        continue;
      }

      // Numeric
      if (field.filterType === FilterType.Numeric) {

        if (controlValue === null || controlValue === undefined || controlValue === '') {
          continue;
        }

        const num = Number(controlValue);
        if (Number.isNaN(num)) {
          continue;
        }

        result.push({
          propertyName: field.fieldKey,
          operator: field.operator,
          values: [String(num)]
        });

        continue;
      }

    }

    return result;
  }

  private getStorageKey(): string | null {
    return getFiltersPanelStorageKey({
      storageKeyStrategy: this.storageKeyStrategy as any,
      tableName: this.tableName,
      routerUrl: this.router.url
    });
  }

  private restoreFromStorageAndEmit(): boolean {
    const stored = this.loadStoredState();
    this.lastStoredState = stored;

    if (!stored) {
      return false;
    }

    // 1) Patch values
    const patch: Record<string, any> = {};

    for (const field of this.fields) {
      const savedValue = stored.valuesByFieldKey?.[field.fieldKey];

      if (savedValue === undefined) {
        continue;
      }

      // Date range: primeNg expects Date[]
      if (field.filterType === FilterType.Date) {

        if (!Array.isArray(savedValue) || savedValue.length === 0) {
          continue;
        }

        const startIso = savedValue[0];
        const endIso = savedValue.length > 1 ? savedValue[1] : null;

        // Convert ISO strings back to Date objects for p-datepicker (range mode)
        const startDate = startIso ? new Date(startIso) : null;
        const endDate = endIso ? new Date(endIso) : null;

        if (!startDate) {
          continue;
        }

        patch[field.controlName] = endDate ? [startDate, endDate] : [startDate];
        continue;
      }

      if (field.filterType === FilterType.Numeric) {
        const n = Number(savedValue);
        if (Number.isNaN(n)) continue;
        patch[field.controlName] = n;
        continue;
      }

      patch[field.controlName] = savedValue;
    }

    this.form.patchValue(patch, { emitEvent: false });

    // 2) UX: If there are option stubs, merge them into field.options to look properly
    this.applyStoredOptionStubsToFields(stored);

    // 3) Emit restore once
    const filters = this.mapFormToBackendFilters();
    if (filters.length > 0) {
      this.isOpen = true;

      Promise.resolve().then(() => {
        this.panelChange.emit({ source: 'restore', filters });
      });

      return true;
    }

    return false;
  }

  private applyStoredOptionStubsToFields(stored: StoredFiltersPanelState) {
    const stubsByFieldKey = stored.optionStubsByFieldKey ?? {};
    if (!stubsByFieldKey) return;

    for (const field of this.fields) {
      if (field.filterType !== FilterType.CustomDropdown) {
        continue;
      }

      const stubs = stubsByFieldKey[field.fieldKey];
      if (!stubs || stubs.length === 0) {
        continue;
      }

      const overrideOptions = Array.isArray(field.options) ? field.options : [];
      const optionValueKey = field.optionValue ?? 'id';

      const merged: any[] = [];
      const existingIds = new Set(overrideOptions.map((o: any) => o?.[optionValueKey]));

      for (const stub of stubs) {
        if (!existingIds.has(stub.id)) {
          merged.push(stub);
        }
      }

      for (const opt of overrideOptions) {
        merged.push(opt);
      }

      field.options = merged;
    }
  }

  private persistToStorage() {
    const storageKey = this.getStorageKey();

    if (!storageKey) {
      console.warn('[FiltersPanel] Missing storage key. State restore/persist is disabled.');
      return;
    }

    const valuesByFieldKey: Record<string, any> = {};
    const optionStubsByFieldKey: DropdownOptionStubsByFieldKey = {};

    const raw = this.form.getRawValue();

    for (const field of this.fields) {
      const value = raw[field.controlName];

      if (field.filterType === FilterType.Text) {
        const text = String(value ?? '').trim();
        if (text.length === 0) continue;

        valuesByFieldKey[field.fieldKey] = text;
        continue;
      }

      if (field.filterType === FilterType.Date) {
        // store the raw range array (Date objects become ISO strings after JSON stringify)
        if (!value) continue;
        valuesByFieldKey[field.fieldKey] = value;
        continue;
      }

      if (field.filterType === FilterType.CustomDropdown) {
        const ids = Array.isArray(value) ? value : [];
        if (ids.length === 0) continue;

        valuesByFieldKey[field.fieldKey] = ids;

        // Save label stubs for remote dropdowns
        const stubs = this.createSelectedOptionLabelStubs(field, ids);
        if (stubs.length > 0) {
          optionStubsByFieldKey[field.fieldKey] = stubs;
        }

        continue;
      }

      if (field.filterType === FilterType.Numeric) {
        if (value === null || value === undefined || value === '') continue;

        const num = Number(value);
        if (Number.isNaN(num)) continue;

        valuesByFieldKey[field.fieldKey] = num;
        continue;
      }

    }

    const state = persistFiltersPanelState({
      storageKey,
      valuesByFieldKey,
      optionStubsByFieldKey
    });

    this.lastStoredState = state ?? null;
  }

  private createSelectedOptionLabelStubs(field: FiltersPanelFieldDefinition, selectedIds: DropdownOptionId[]): DropdownOptionLabelStub[] {
    const stubs: DropdownOptionLabelStub[] = [];

    const options = Array.isArray(field.options) ? field.options : [];
    const optionLabelKey = field.optionLabel ?? 'text';
    const optionValueKey = field.optionValue ?? 'id';

    for (const id of selectedIds) {
      const match = options.find((opt: any) => opt?.[optionValueKey] === id);

      if (match) {
        stubs.push({ id: match[optionValueKey], text: match[optionLabelKey] });
        continue;
      }

      stubs.push({ id, text: `#${id}` });
    }

    return stubs;
  }

  private loadStoredState(): StoredFiltersPanelState | null {
    const storageKey = this.getStorageKey();

    if (!storageKey) {
      return null;
    }

    return loadStoredFiltersPanelState(storageKey);
  }

  private clearFromStorage() {
    const storageKey = this.getStorageKey();

    if (!storageKey) {
      return;
    }

    clearFiltersPanelState(storageKey);
    this.lastStoredState = null;
  }
}
